import React from 'react';
import { BookOpen, Users, Calendar, Layers } from 'lucide-react';
import './InstructionsPage.css';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { getDirectionTerms } from '../../utils/organizationHelpers';

const InstructionsPage = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);
    const telegramBotUrl = config?.telegramBotUrl || 'https://t.me/calendar_mag_bot';
    const telegramBotUsername = config?.telegramBotUsername || 'calendar_mag_bot';

    const handleCopyEmail = async (email) => {
        if (!email) return;
        try {
            await navigator.clipboard.writeText(email);
        } catch (e) {
            console.error('Не удалось скопировать email:', e);
        }
    };

    const emailHint = user?.email
        ? 'Ниже указан ваш текущий email — именно его нужно отправить боту (без лишних пробелов и символов).'
        : 'Отправьте в бот тот email, который указан в вашем профиле календаря.';

    return (
        <div className="instructions-page">
            <div className="instructions-header">
                <BookOpen size={32} />
                <div>
                    <h1>Инструкция</h1>
                </div>
            </div>

            <div className="instructions-content">


                {/* Работа с группами */}
                <section className="instruction-section">
                    <h2>📝 Работа с группами</h2>
                    <p>
                        Для управления группами перейдите на страницу{' '}
                        <a href="/groups" style={{ color: '#1976d2', fontWeight: 'bold' }}>Группы</a>.

                    </p>
                    <p>
                        У кажой группы справа есть столбец "Действия" с кнопками для управления группой. Наводите курсор на иконки кнопок, чтобы увидеть подсказки о том, что они означают.
                        Настроить, Расписание, Заявки, Публичная страница, Удалить

                    </p>

                    <div className="step-list">
                        <div className="step-item">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h4>Создайте группу</h4>
                                <p>Заполните основную информацию:</p>
                                <ul>
                                    <li><strong>Внутреннее наименование</strong> — рабочее название для удобства</li>
                                    <li><strong>Публичное название</strong> — название для публичной страницы</li>
                                    <li><strong>{directionTerms.one}</strong> — тип обучения</li>
                                    <li><strong>Регион</strong> — место проведения</li>
                                    <li><strong>Формат</strong> — онлайн, офлайн или смешанный</li>
                                    <li><strong>И т.д.</strong></li>
                                </ul>
                                <p style={{ marginTop: '10px', padding: '10px', background: '#fff9c4', borderLeft: '4px solid #ffc107', borderRadius: '4px' }}>
                                    <strong>💡 Важно:</strong> Группы, выделенные <span style={{ background: '#fff9c4', padding: '2px 6px', borderRadius: '3px' }}>жёлтым</span>,
                                    это неподтверждённые администратором группы.
                                </p>
                            </div>
                        </div>

                        <div className="step-item">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h4>Настройте статус</h4>
                                <p>Выберите актуальный статус группы:</p>
                                <ul>
                                    <li><span className="status-open">Набор открыт</span> — идет активный набор</li>
                                    <li><strong>Набор закрыт</strong> — заявки не принимаются</li>
                                    <li><span className="status-additional">Группа запущена, донабор</span> — группа уже стартовала, но еще набираем</li>
                                    <li><span className="status-closed">Группа запущена</span> — обучение идет, набор закрыт</li>
                                    <li>Группа завершена — обучение закончено</li>
                                    <li>Группа не запустилась — группа отменена</li>
                                </ul>
                            </div>
                        </div>

                        <div className="step-item">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h4>Добавьте расписание</h4>
                                <p>Нажмите кнопку "Расписание" и:</p>
                                <ul>
                                    <li>Укажите даты и темы занятий</li>
                                    <li>Назначьте преподавателей на каждое занятие</li>
                                    <li>Проверьте отсутствие конфликтов в календаре</li>
                                    <li>Самая ранняя дата станет датой старта</li>
                                </ul>
                            </div>
                        </div>

                        <div className="step-item">
                            <div className="step-number">4</div>
                            <div className="step-content">
                                <h4>Настройте тренеров</h4>
                                <p>Предоставьте доступ коллегам:</p>
                                <ul>
                                    <li>Выберите пользователей для совместной работы</li>
                                    <li>Настройте права редактирования</li>
                                    <li>Включите уведомления о заявках</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '20px', padding: '25px', background: '#e3f2fd', borderLeft: '4px solid #2196f3', borderRadius: '4px' }}>
                        <h4 style={{ marginTop: 0, color: '#1565c0' }}>⚠️ Подтверждение группы администратором</h4>
                        <p><strong>Что происходит при подтверждении:</strong></p>
                        <ul style={{ marginBottom: '10px' }}>
                            <li>Группе автоматически присваивается кодовое имя (например: <strong>ГТ/МСК/1</strong>)</li>
                            <li>Кодовое имя формируется на основе {directionTerms.oneGen} и региона</li>
                        </ul>
                        <p style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: 0 }}>
                            ❌ После подтверждения группу нельзя удалить! Можно только изменить её статус или параметры.
                        </p>
                    </div>

                    <div style={{ marginTop: '20px', padding: '25px', background: '#f3e5f5', borderLeft: '4px solid #9c27b0', borderRadius: '4px' }}>
                        <h4 style={{ marginTop: 0, color: '#6a1b9a' }}>👁️ Видимость группы на публичной странице</h4>
                        <p><strong>Группа показывается на публичной странице только если у неё добавлено расписание и выполняются условия:</strong></p>
                        <ul style={{ marginBottom: 0 }}>
                            <li><strong>Набор открыт</strong> — показывается, если дата старта группы ещё не прошла</li>
                            <li><strong>Группа запущена, донабор</strong> — показывается, если последняя дата в расписании ещё не прошла</li>
                            <li><strong>Набор закрыт</strong> — не показывается в списке набора, заявку подать нельзя</li>
                            <li><strong>Группа запущена</strong> — не показывается на публичной странице</li>
                            <li><strong>Группа завершена</strong> — не показывается на публичной странице</li>
                            <li><strong>Группа не запустилась</strong> — не показывается на публичной странице</li>
                            <li><strong>Группа у которых выбран формат (регулярно)</strong> — показываются на публичной странице даже после того как прошла последняя указанная дата и вместо расписание у них отображается их регулярный формат</li>

                        </ul>
                    </div>
                </section>

                {/* Управление заявками */}
                <section className="instruction-section">
                    <h2>✉️ Управление заявками</h2>
                    <p>
                        Для того чтобы открыть заявки группы, перейдите в раздел <a href="/groups" style={{ color: '#1976d2', fontWeight: 'bold' }}>Мои группы</a>,
                        найдите нужную группу, нажмите на кнопку "Настроить" и в открывшемся окне зайдите в раздел "Тренеры". Каждый пользователь добавленный в этот раздел будет иметь доступ к заявкам этой группы.
                    </p>
                    <p>
                        <strong style={{ color: '#000' }}>Наводите курсор на иконки кнопок, чтобы увидеть подсказки о том, что они означают.</strong>
                    </p>
                    <ul>
                        <li>Просматривайте все заявки на странице <a href="/applications" style={{ color: '#1976d2', fontWeight: 'bold' }}>Заявки</a></li>
                        <li>Меняйте статус: "Подтверждена", "Отклонена", "Связались",</li>

                    </ul>
                </section>

                {/* Работа с календарем */}
                <section className="instruction-section">
                    <h2>📅 Работа с календарем</h2>
                    <p>
                        <a href="/calendar" style={{ color: '#1976d2', fontWeight: 'bold' }}>Календарь</a> показывает занятость всех преподавателей в табличном виде.
                        Переключайтесь между периодами, проверяйте конфликты и просматривайте детали событий при наведении курсора.
                    </p>
                </section>

                {/* Работа с календарем */}
                <section className="instruction-section">
                    <h2>📅 Работа с профилем</h2>
                    <p>
                        <a href="/profile" style={{ color: '#1976d2', fontWeight: 'bold' }}>Профиль</a> Здесь вы можете изменить своё имя, фото, описание и пароль.
                    </p>
                </section>

                {/* Telegram-уведомления */}
                <section className="instruction-section">
                    <h2>📨 Telegram-уведомления</h2>
                    <p>
                        Система может дублировать уведомления (новые заявки, вопросы, записи на консультации) в Telegram.
                    </p>
                    <ol>
                        <li>
                            <strong>Откройте бота в Telegram:</strong>{' '}
                            <a
                                href={telegramBotUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: '#1976d2', fontWeight: 'bold' }}
                            >
                                @{telegramBotUsername}
                            </a>
                            .
                        </li>
                        <li>
                            <strong>Нажмите «Start» в боте</strong> или кнопку <strong>«👤 Мой статус»</strong>, чтобы увидеть подсказку.
                        </li>
                        <li>
                            <strong>Отправьте в бот ваш email из календаря</strong> в точности так, как он указан в вашем профиле.
                            {user?.email && (
                                <>
                                    {' '}Сейчас у вас указан:{' '}
                                    <button
                                        type="button"
                                        onClick={() => handleCopyEmail(user.email)}
                                        style={{
                                            border: 'none',
                                            background: 'none',
                                            padding: 0,
                                            margin: 0,
                                            cursor: 'pointer',
                                            color: '#1976d2',
                                            fontWeight: 'bold',
                                            textDecoration: 'underline'
                                        }}
                                        title="Нажмите, чтобы скопировать email"
                                    >
                                        {user.email}
                                    </button>
                                </>
                            )}
                        </li>
                        <li>
                            После успешной привязки бот ответит, что Telegram-уведомления подключены, и укажет ваше имя / email.
                        </li>
                        <li>
                            В профиле календаря вы увидите статус, что Telegram подключен, и сможете в любой момент нажать
                            кнопку «Отвязать Telegram».
                        </li>
                    </ol>
                    <p>{emailHint}</p>
                    <p>
                        Уведомления по-прежнему дублируются на почту.
                    </p>
                </section>


            </div>
        </div>
    );
};

export default InstructionsPage;

